import { createApp, App as VueApp, nextTick } from "vue";
import { createPinia, Pinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import App from "@/App.vue";
import router from "./router";
import Util from "@/utils/util";
import { RouteHelper } from "@/utils/routeHelper";
import { initAppEnvironment } from "@/utils/initAppEnvironment";
import { initializeEnvironment } from "@/utils/initializeEnvironment";
import lazyLoad from "vue-lazyload";
import AppProtocol from "@/utils/appProtocol";
import { openProtocol } from "@babybus/lib-protocol";
import uploader from "vue-simple-uploader";
// @ts-ignore: allow importing css for side-effects (no type declarations)
import "vue-simple-uploader/dist/style.css";
// 导入 Toast 插件
import ToastPlugin from "@/plugins/toast";
import ModalPlugin from "@/plugins/modal";
import AnalysisPlugin, { AnalysisService } from "@/plugins/analysis";
import { TOAST_NO_NETWORK } from "@/plugins/toast";

// 导入 Pinia stores
import { useUserStore, useGlobalStore } from "@/stores/index";

// 创建Vue实例
const app: VueApp = createApp(App);
const pinia: Pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const grayPlaceholder =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="100" viewBox="0 0 1 100">
      <rect width="1" height="100" fill="#fff"/>
    </svg>`,
  );

// 使用插件
app.use(router);
app.use(pinia);
app.use(AnalysisPlugin);
app.use(lazyLoad, {
  preLoad: 1.2,
  attempt: 1,
  loading: grayPlaceholder,
});
app.use(ToastPlugin);
app.use(ModalPlugin);
app.use(uploader);

// 获取 Pinia store 实例
const userStore = useUserStore();
const globalStore = useGlobalStore();

// 注入小指令
import vHover from "./directives/v-hover";
app.directive("hover", vHover);

// 全局属性
app.config.globalProperties.$AppProtocol = AppProtocol;

// App登录相关
Object.defineProperty(window, "state", {
  value: {
    stateMessage: function () {
      console.debug("on state message");
      // if (userStore.isLogin != 1) {
      onAppLogin();
      // }
      return 0;
    },
  },
  writable: true,
  configurable: true,
});

var afterLoginRoutePushData: { routeList: any[]; isNavigating: boolean } = {
  routeList: [],
  isNavigating: false,
};
let hasReportedMallOpen = false;
// App登录后执行
var onAppLogin = async function (): Promise<void> {
  await initAppEnvironment();
  if (userStore.$state.isLogin) {
    const to = JSON.parse(sessionStorage.getItem("app_navigatorAfterLogin") || "{}");
    if (to?.path) {
      router.push(to);
    }
  }
};

// 苹果设备双击后屏幕上移操作禁用
let agent = navigator.userAgent.toLowerCase();
let iLastTouchTime: number | null = null;
if (agent.indexOf("iphone") >= 0 || agent.indexOf("ipad") >= 0) {
  document.querySelector("body").addEventListener(
    "touchend",
    function (event) {
      let iNowTime = new Date().getTime();
      iLastTouchTime = iLastTouchTime || iNowTime + 1;
      let delta = iNowTime - iLastTouchTime;
      if (delta < 500 && delta > 0) {
        // 检查事件是否可取消
        if (event.cancelable) {
          event.preventDefault();
        }
        return false;
      }
      iLastTouchTime = iNowTime;
    },
    false,
  );
}
const isInApp = Util.isInApp();
const isInMiniProgram = Util.isInMiniProgram();

if (isInApp) {
  window.onClickShare = () => {
    console.warn("[onClickShare] 未找到当前页面分享处理器");
  };
}

// 主函数
(async () => {
  // 启动清空routerStorage
  console.debug("getAppVersion: ", Util.getAppVersion());

  // 设置头部信息、登录状态、会员状态
  await initializeEnvironment({
    isInApp,
    isInMiniProgram,
  });

  // 判断是否存在登录后跳转
  const loginRouteList: any[] | null = JSON.parse(localStorage.getItem("loginRouteList") || "[]");

  if (localStorage.getItem("isLoginExecutedRecently") && loginRouteList?.length && userStore.isLogin) {
    // 因为登录后返回存在一个延迟的页面刷新 所以这里晚一点再清除缓存避免先触发后才刷新页面
    setTimeout(() => {
      localStorage.removeItem("isLoginExecutedRecently");
      localStorage.removeItem("loginRouteList");
    }, 1000);
    if (loginRouteList.length > 1) {
      //2层级以上需要从底部开始重导航
      //场景：详情页购买需要登录，登录后回到确认订单页下单
      const loginLoadElement = document.getElementById("login-load");
      if (loginLoadElement) {
        loginLoadElement.style.display = "flex"; // 展示 loading 遮挡跳转

        //延时检查，如果3秒后仍然显示，则强制隐藏
        setTimeout(() => {
          if (loginLoadElement && loginLoadElement.style.display === "flex") {
            console.warn("登录后路由跳转超时，强制隐藏登录加载界面");
            loginLoadElement.style.display = "none";
          }
        }, 3000);
      }
    }
    afterLoginRoutePushData.routeList = loginRouteList;
    afterLoginRoutePushData.isNavigating = false;
  }

  // 挂载应用
  app.mount("#app");
  if (isInApp) {
    nextTick(() => {
      openProtocol("babybus://page/onPageReady", {
        url: window.location.href,
      })
        .then((res) => {
          console.debug("通知 App 页面已就绪成功", res);
        })
        .catch((error) => {
          console.warn("通知 App 页面已就绪失败", error);
        });

      // 与 onPageReady 并行查询，尽早确定是否隐藏 H5 导航栏
      AppProtocol.openBuguProtocol("babybus://bugu/isHideH5TitleBar")
        .then((result) => {
          const response = Util.deserialize(result);
          globalStore.isHideH5TitleBar = response?.status === 1 ? !!response.data : false;
        })
        .catch((error) => {
          console.warn("获取 isHideH5TitleBar 失败，默认展示标题栏", error);
          globalStore.isHideH5TitleBar = false;
        });
    });
  }
  globalStore.fetchGlobalData({ CategoryCode: "MallCenter" });
})();

// 检查当前环境是否为生产环境
if (window.location.href.includes("isDebug")) {
  // 加载 vConsole 调试工具
  var script1 = document.createElement("script");
  script1.src = "https://unpkg.com/vconsole@3.15.1/dist/vconsole.min.js";
  script1.async = true; //"async";
  document.body.appendChild(script1);
  script1.onload = function () {
    new window.VConsole();
  };
}

console.log("环境变量", import.meta.env.VITE_APP_ENV, import.meta.env);
// 路由钩子
router.beforeEach(async (to, from, next) => {
  console.debug("Before Router Update, from", from);
  console.debug("Before Router Update, to", to);

  // 判断目标页是否需要登录
  if (to?.meta?.requireAuth) {
    const isLogin = await userStore.checkLogin();
    if (!isLogin) {
      Util.toLogin(to);
      return next(false);
    }
  }

  // 如果正在导航到首页，处理首页导航逻辑
  if (globalStore.$state.isNavigatingHome) {
    return RouteHelper.handleHomeNavigation(to, next); // 提前返回
  }

  next();

  RouteHelper.updateKeepAliveCache(to, "beforeEach");
});

router.afterEach((to, from, failure) => {
  if (failure) {
    console.log("Navigation was aborted:", failure);
    return;
  }

  if (!hasReportedMallOpen) {
    hasReportedMallOpen = true;
    // iOS WKWebView 下 JSBridge 就绪较慢，延迟首条埋点确保公共参数已就绪
    setTimeout(() => {
      AnalysisService.event("用户活跃-打开商城", {
        Page: to.meta?.pageName ? String(to.meta.pageName) : "",
      });
    }, 300);
  }

  console.timeEnd(`Route Navigation to ${String(to.name)}`);
  RouteHelper.updateKeepAliveCache(to, "afterEach");
  RouteHelper.updateHistoryStack(to);
  RouteHelper.loginBackRouteCheck(afterLoginRoutePushData);
  if (!to?.meta.customNavBarTitle) {
    const title = typeof to.meta?.title === "string" ? to.meta.title : "";
    Util.debounce(
      function () {
        Util.setPageTitle(title);
      },
      1000,
      true,
    )();
  }
});

router.onError((error) => {
  console.error("---router.onError---", error);
  // 修改正则表达式，同时匹配 JS chunk 和 CSS chunk 加载失败的错误
  const css_failed_pattern = /Loading (CSS )?chunk .+ failed/g;
  const pattern = /Failed to fetch dynamically imported module/gi;
  const module_failed_pattern = /importing a module script failed/gi;
  const isChunkCSSLoadFailed = error.message.match(css_failed_pattern);
  const isChunkLoadError = pattern.test(error.message) || module_failed_pattern.test(error.message);

  if (isChunkCSSLoadFailed || isChunkLoadError) {
    // 刷新页面尝试恢复
    app.config.globalProperties.$showToast("页面有更新，请刷新页面或重新进入~");
    window.location.reload();
  } else {
    app.config.globalProperties.$showToast("加载失败，请联系客服");
  }
});
